import React, { useState, useEffect, ReactChild } from "react"
import { PhotoItem, SelectionManager, SelectionListener, locale } from "@albelli/photoselector"

interface ItemProps {
  item: PhotoItem
  store: SelectionManager // TODO: should be used with proper store manager instead
  bulkAdapter: () => PhotoItem[]
  children?: ReactChild | ReactChild[]
}

// Component that renders single item. Handles selection, title and image preview ect.
export const PhotoItemContainer: React.FunctionComponent<ItemProps> = ({ item, store, children, bulkAdapter }) => {
  // set image as background
  const style: React.CSSProperties = { backgroundImage: `url(${item.urlThumbnail})` }
  // remove pointer if not usable. otherwise it will confuse users
  if (item.notUsable) {
    style.cursor = "default"
  }
  // this state is needed for handling selection of the item
  const [isSelected, setIsSelected] = useState<boolean>(false)
  // method to handle selection event emittion
  // in case item is selected somewhere else, we need to do UI update
  const listener: SelectionListener = (action) => {
    switch (action) {
      case "set":
        setIsSelected(store.isSelected(item))
        break
      case "select":
      case "select_list":
        const isSelected = store.isSelected(item)
        if (isSelected) {
          setIsSelected(true)
        }
        break
      case "deselect":
      case "deselect_list":
        const isDeselected = !store.isSelected(item)
        if (isDeselected) {
          setIsSelected(false)
        }
        break
    }
  }

  useEffect(() => {
    // on first render, set initial value of selection and add listeners
    setIsSelected(store.isSelected(item))
    // start changing here
    store.addListener(listener)
    return () => {
      store.removeListener(listener)
    }
  }, [])

  // handles selection of the item on click
  const handleClick = (event: React.MouseEvent) => {
    if (item.notUsable) {
      return
    } else if (bulkAdapter && event.shiftKey) {
      // bulk adapter returns list of all items. on shift key we do range select
      // from start until selected item
      store.rangeSelect(item, bulkAdapter())
    } else if (isSelected) {
      // if already selected item clicked, we diselect it
      store.deselect(item)
    } else {
      // otherwise select item
      store.select(item)
    }
  }

  const itemClass = `photoselector-photo-organiser__item ${item.notUsable ? "disable" : ""} ${isSelected ? "selected" : ""}`

  return (
    <div className={itemClass} style={style} data-tam="image-item" id={item.id} onClick={handleClick}>
      {item.notUsable ? <WarningOverlay /> : <Overlays isSelected={isSelected}>{children}</Overlays>}
    </div>
  )
}

const Overlays: React.FunctionComponent<{ isSelected: boolean }> = ({ children, isSelected }) => {
  return (
    <>
      {children}
      <div className={isSelected ? "photoselector-selection-overlay" : "photoselector-selection-overlay-not-selected"} data-tam="selection-overlay" />
    </>
  )
}

const WarningOverlay: React.FunctionComponent = () => {
  return (
    <>
      <div className="photoselector-item-dark-overlay"></div>
      <div className="photoselectot-item-warning-not-used">
        <span className="warning"></span>
        {locale.get().upload.errors.INVALID_IMAGE}
      </div>
    </>
  )
}
